import userService from "@/services/userService"
import api from "@/api"
import healthConnect from "./healthConnectService"
import trainingFeatureAccess from "./trainingFeatureAccess"
import ownHealthFeatureAccess from "./ownHealthFeatureAccess"

const messages = {
  HEALTH_BUSY: "Die Freigabe für Apple Health ist noch geöffnet.",
  HEALTH_AUTHORIZATION_INCOMPLETE: "Die Freigabe wurde nicht abgeschlossen. Du kannst es erneut versuchen.",
  HEALTH_WORKOUT_UNAVAILABLE: "Dieses Uhrtraining ist nicht mehr freigegeben oder wurde gelöscht. Bitte erneut suchen.",
  HEALTH_READ_FAILED: "Apple Health konnte nicht gelesen werden. Entsperre dein iPhone und versuche es erneut.",
}

async function nativeCall(method, payload = {}) {
  // Android uses the own-data pilot grant. The iPhone branch below keeps its
  // existing access and import behavior until the separate Apple rollout.
  if (healthConnect.isAndroid()) return healthConnect.native(method, payload, method === "healthImportAuthorize" ? 300000 : 30000)
  const access = trainingFeatureAccess.capture()
  if (access === null) return { supported: false }
  const userId = String(userService.getUserId() || "")
  const token = window.__wellcomeHealthImportToken
  if (!userId || !token || !window.flutter_inappwebview?.callHandler) return { supported: false }
  let timeout
  try {
    const response = await Promise.race([
      window.flutter_inappwebview.callHandler(method, { ...payload, bridgeToken: token }),
      new Promise((_, reject) => {
        timeout = setTimeout(() => reject(new Error("Apple Health antwortet noch nicht. Bitte erneut versuchen.")), method === "healthImportAuthorize" ? 300000 : 30000)
      }),
    ])
    if (!trainingFeatureAccess.isCurrent(access) || String(userService.getUserId() || "") !== userId || token !== window.__wellcomeHealthImportToken) {
      throw new Error("Die Anmeldung hat sich geändert. Bitte das Training erneut öffnen.")
    }
    if (response?.accepted === false) throw new Error(messages[response.code] || response.error || messages.HEALTH_READ_FAILED)
    return response || { supported: false }
  } finally { clearTimeout(timeout) }
}

function requireSupported(result) {
  if (result?.supported !== true) throw new Error("Bitte die aktuelle App installieren und Gesundheitsdaten im Trainingshub verbinden.")
  return result
}

export default {
  isAndroid: () => healthConnect.isAndroid(),
  async available() {
    if (healthConnect.isAndroid()) return healthConnect.importAvailable()
    if (!trainingFeatureAccess.isAllowed()) return false
    try { return (await nativeCall("healthImportCapabilities"))?.supported === true } catch (_) { return false }
  },
  async authorize() { return requireSupported(await nativeCall(healthConnect.isAndroid() ? "healthImportCapabilities" : "healthImportAuthorize")) },
  async search(range, { allowOffline = false } = {}) {
    const permission = healthConnect.isAndroid() ? ownHealthFeatureAccess : trainingFeatureAccess
    const access = permission.capture()
    const userId = String(userService.getUserId() || "")
    const result = requireSupported(await nativeCall("healthImportSearch", range))
    if (!permission.isCurrent(access) || userId !== String(userService.getUserId() || "")) throw new Error("Die Anmeldung hat sich geändert. Bitte erneut suchen.")
    const workouts = Array.isArray(result.workouts) ? result.workouts : []
    // Search sends only IDs. Health Connect associations also send only the ID;
    // measurements are loaded on the owning phone when its training is opened.
    let availability = { links: [] }, linkStatusAvailable = true
    if (workouts.length) {
      try { availability = await api.workoutApi.getHealthWorkoutLinks(workouts.map((item) => item.workoutId)) }
      catch (error) {
        await permission.reject(access, error)
        if (!allowOffline || !permission.isCurrent(access)) throw error
        linkStatusAvailable = false
      }
    }
    if (!permission.isCurrent(access) || userId !== String(userService.getUserId() || "")) throw new Error("Die Anmeldung hat sich geändert. Bitte erneut suchen.")
    return { workouts: workouts.map((item) => ({ ...item,
      existingLink: (availability.links || []).find((link) => link.workoutId === item.workoutId) || null,
      dismissedExecutionIds: (availability.dismissals || []).filter(link => link.workoutId === item.workoutId).map(link => String(link.executionId)),
    })), truncated: result.truncated === true, automaticLinking: availability.automaticLinking === true, linkStatusAvailable,
      readableFrom: typeof result.readableFrom === "string" && Number.isFinite(Date.parse(result.readableFrom)) ? result.readableFrom : null }
  },
  async read(workoutId) {
    return requireSupported(await nativeCall("healthImportRead", { workoutId })).workout
  },
}
